import { CreateTravellerPayload } from 'lib/react-query/Queries/useCreateTraveller';
import { SubmitTravellerOptions, TravellerFormUser } from 'lib/types/traveller';
import { SelectionAvailability } from 'lib/types/availability';
import { SelectedRoom } from 'lib/types/trip';
import { FlexibleDepartureDecision } from 'lib/types/product';
import { useCustomNavigate } from './useCustomNavigate';
import { stagePathConfig } from 'data/stage';
import { useTenant } from './useTenant';
import useTripQuery from 'lib/react-query/Queries/useTripQuery';
import { useSessionLockStatus } from '@/lib/react-query/Queries/useSessionLockStatus';

type Props = {
  state: TravellerFormUser;
  validateTraveller: () => boolean | undefined;
  submit: (
    submitData: CreateTravellerPayload,
    options?: SubmitTravellerOptions
  ) => void;
  selectedRooms: SelectedRoom[];
};

const useSubmitTraveller = ({
  state,
  validateTraveller,
  submit,
  selectedRooms,
}: Props) => {
  const navigate = useCustomNavigate();
  const { isDA } = useTenant();
  const { data: tripData } = useTripQuery();
  const { isLocked } = useSessionLockStatus();

  const continueHandler = () => {
    navigate(stagePathConfig.payment);
  };

  // DealsAway: Traveller -> Rooms -> Payment. Rooms is skipped when the trip
  // has no room options or the session is locked.
  const dealsAwayContinueHandler = () => {
    const hasRoomStage =
      Boolean(tripData?.result.rooms && tripData.result.rooms.length > 0) &&
      !isLocked;
    navigate(hasRoomStage ? stagePathConfig.rooms : stagePathConfig.payment);
  };

  function submitTraveller(
    isFlexible?: boolean,
    departureDate?: SelectionAvailability,
    flexibleDepartureDecision?: FlexibleDepartureDecision | null
  ) {
    const isValid = validateTraveller();
    if (!isValid) return;

    // GWAT moves to Payment straight away. DealsAway waits for setup to
    // succeed so the sales register exists before Rooms calls setup again.
    if (!isDA) {
      continueHandler();
    }
    const payload: CreateTravellerPayload = {
      email: state.email,
      firstName: state.firstName,
      lastName: state.lastName,
      phonePrefix: state.phonePrefix?.label,
      phoneNumber: state.phoneNumber,
      // NOTE: We have checked for date not being null in the validation
      // above, so we provide `!` as typescript is dumb to understand it
      dateOfBirth: state.date!,
      flexibleDeparture: isFlexible,
      flexibleDepartureDecision,
      selectedAvailability: departureDate,
      selectedRooms,
    };

    if (isDA) {
      submit(payload, { onSuccess: dealsAwayContinueHandler });
    } else {
      submit(payload);
    }
  }

  return { submitTraveller };
};

export { useSubmitTraveller };
